import { Component, OnInit, OnDestroy } from '@angular/core';
import { CrudService } from '@/shared/services/crud.service';
import { ProductService } from '@/shared/services/product.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
    catchError,
    distinctUntilChanged,
    map,
    Subject,
    takeUntil,
    throwError,
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DeviceCheckService } from 'src/app/viewbid/shared/services/device-check.service';
import {
    navigateProfilePaging,
    ProfilePaging,
    resolveProfilePaging,
} from 'src/app/viewbid/shared/utils/profile-paging.util';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
    fmtDate,
    VbLegendItem,
    VbMenuItem,
    VbMessageTableMenuEvent,
    VbMessageTableRow,
    VbSnackbarService,
} from '@viewbid/ui';

/**
 * Minimal shape of one thread row returned by
 * `/api/notifications/user-messages` — only the fields this panel reads
 * (the payload carries more).
 */
interface UserMessageComment {
    id: string | number;
    /** Raw topic key — forwarded to the thread modal. */
    topic: string;
    /** Human-readable topic shown in the table. */
    topicLabel: string;
    topicId: string;
    accountMappingsId: string;
    createdBy: string;
    /**
     * 'Read' | 'Unread' from the server; remapped to 'Read' in loadPage()
     * when the latest message in the thread is the user's own.
     */
    status: string;
    /** Latest reply text in the thread. */
    comments: string;
    createdOn: string;
    /** Context ids (ISSUE_WITH_ORDER / ASK_ABOUT_LOT) — forwarded to the
     *  thread modal, which resolves them into a lot / invoice link. Null on
     *  threads created before context ids were stored. */
    invoiceId?: string;
    auctionId?: string;
    lotId?: string;
}

@Component({
    selector: 'vb-user-messages',
    templateUrl: './user-messages.component.html',
    styleUrls: ['./user-messages.component.scss'],
})
export class UserMessagesComponent implements OnInit, OnDestroy {
    public comments: Array<UserMessageComment> = [];
    public paginate: any = {};
    public pageNo: number = 1;
    public pageSize: number = 10;
    private readonly defaultPageSize: number = this.pageSize;
    public readonly pageSizeOptions: ReadonlyArray<number> = [10, 25, 50];
    public isLoading: boolean = true;
    private mobile$ = new Subject<void>();
    private destroy$ = new Subject<void>();
    public isMobile: boolean = false;

    /** View rows for the vb-message-table, rebuilt on every loadPage(). */
    public rows: VbMessageTableRow[] = [];

    public readonly legendItems: VbLegendItem[] = [
        { tone: 'danger', label: 'Unread' },
        { tone: 'neutral', label: 'Read' },
    ];

    /** Row actions for the table's embedded kebab menu. */
    public readonly rowMenuItems: VbMenuItem[] = [
        { id: 'delete', label: 'Delete conversation', danger: true },
    ];

    /** Delete-conversation confirm (vb-dialog) — replaces the legacy
     *  ngx-bootstrap ConfirmationWindowComponent for this flow. */
    deleteConfirmOpen: boolean = false;
    private pendingDeleteTopicId: string | null = null;

    /** Thread dialog (vb-message-thread composes the vb-dialog shell) —
     *  replaces the legacy ngx-bootstrap thread modal. */
    threadOpen: boolean = false;
    threadComment: UserMessageComment | null = null;

    constructor(
        public productService: ProductService,
        private crudService: CrudService,
        public snackbarService: VbSnackbarService,
        public translate: TranslateService,
        private deviceCheckService: DeviceCheckService,
        private route: ActivatedRoute,
        private router: Router,
    ) {}

    ngOnInit(): void {
        this.route.queryParams
            .pipe(
                map((params: Params): ProfilePaging => {
                    navigateProfilePaging(
                        this.router,
                        this.route,
                        params,
                        this.defaultPageSize,
                        true,
                    );
                    return resolveProfilePaging(params, this.defaultPageSize);
                }),
                distinctUntilChanged(
                    (previous: ProfilePaging, current: ProfilePaging) =>
                        previous.page === current.page &&
                        previous.pageSize === current.pageSize,
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((paging: ProfilePaging) => {
                this.pageNo = paging.page;
                this.pageSize = paging.pageSize;
                this.loadPage();
            });

        this.deviceCheckService.isMobile$
            .pipe(takeUntil(this.mobile$))
            .subscribe((isMobile: boolean) => {
                this.isMobile = isMobile;
            });
    }

    loadPage(): void {
        const paging: ProfilePaging = {
            page: this.pageNo,
            pageSize: this.pageSize,
        };
        this.isLoading = true;
        this.crudService
            .fetchData(
                `/api/notifications/user-messages?page=${paging.page}&size=${paging.pageSize}`,
            )
            .pipe(
                catchError((HttpError: HttpErrorResponse): any => {
                    if (
                        paging.page !== this.pageNo ||
                        paging.pageSize !== this.pageSize
                    ) {
                        return throwError(() => HttpError);
                    }
                    this.isLoading = false;
                    this.snackbarService.error(
                        this.translate.instant('MyMessages.getMessagesError'),
                    );
                    return throwError(() => HttpError);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (comments: any) => {
                    if (
                        paging.page !== this.pageNo ||
                        paging.pageSize !== this.pageSize
                    ) {
                        return;
                    }
                    const correctedPaging: ProfilePaging = resolveProfilePaging(
                        paging,
                        this.defaultPageSize,
                        comments.totalCount ?? 0,
                    );
                    if (correctedPaging.page !== paging.page) {
                        navigateProfilePaging(
                            this.router,
                            this.route,
                            correctedPaging,
                            this.defaultPageSize,
                            true,
                        );
                        return;
                    }
                    this.paginate = this.productService.getPager(
                        comments.totalCount ?? 0,
                        paging.page,
                        paging.pageSize,
                        false,
                    );
                    this.isLoading = false;
                    this.comments = comments.data.map(
                        (comment: UserMessageComment) => ({
                            ...comment,
                            status:
                                comment.accountMappingsId === comment.createdBy
                                    ? 'Read'
                                    : comment.status,
                        }),
                    );
                    this.rows = this.comments.map(
                        (comment: UserMessageComment) =>
                            this.toTableRow(comment),
                    );
                },
            });
    }

    /** Whole-row activation from the table — open the thread modal. */
    onRowClick(row: VbMessageTableRow): void {
        const comment: UserMessageComment | undefined = this.findComment(
            row.id,
        );
        if (comment) {
            this.onLoadTopic(comment);
        }
    }

    /** Selection from a row's embedded kebab menu. */
    onRowMenuSelect(event: VbMessageTableMenuEvent): void {
        if (event.itemId !== 'delete') return;
        const comment: UserMessageComment | undefined = this.findComment(
            event.row.id,
        );
        if (comment) {
            this.deleteConversation(comment.topicId);
        }
    }

    onLoadTopic(comment: UserMessageComment): void {
        this.threadComment = comment;
        this.threadOpen = true;
    }

    /** Thread dialog dismissed — reload so read-state changes surface (the
     *  legacy modal did the same via its response emit). */
    onThreadClosed(): void {
        this.threadOpen = false;
        this.loadPage();
    }

    crudError(HttpError: HttpErrorResponse): void {
        this.snackbarService.dismissAll();
        this.snackbarService.error(
            HttpError.error.message
                ? HttpError.error.message
                : HttpError.message,
        );
    }

    setPage(page: number): void {
        navigateProfilePaging(
            this.router,
            this.route,
            { page, pageSize: this.pageSize },
            this.defaultPageSize,
        );
    }

    setPageSize(pageSize: number): void {
        navigateProfilePaging(
            this.router,
            this.route,
            { page: 1, pageSize },
            this.defaultPageSize,
        );
    }

    deleteConversation(topicId: any): void {
        this.pendingDeleteTopicId = String(topicId);
        this.deleteConfirmOpen = true;
    }

    /** Same translated copy the legacy confirmation window showed. */
    get deleteConfirmTitle(): string {
        return this.translate.instant('MyMessages.delete.headerTitle');
    }

    get deleteConfirmMessage(): string {
        return this.translate.instant('MyMessages.delete.mainText');
    }

    get deleteConfirmWarning(): string {
        return this.translate.instant('MyMessages.delete.warningText');
    }

    get deleteConfirmLabel(): string {
        return this.translate.instant('MyMessages.delete.buttonLabel');
    }

    closeDeleteConfirm(): void {
        this.deleteConfirmOpen = false;
        this.pendingDeleteTopicId = null;
    }

    onDeleteConfirmed(): void {
        const topicId: string | null = this.pendingDeleteTopicId;
        this.closeDeleteConfirm();
        if (topicId) this.completeDeleteConversation(topicId);
    }

    private toTableRow(comment: UserMessageComment): VbMessageTableRow {
        return {
            id: String(comment.id),
            topic: comment.topicLabel || comment.topic,
            unread: (comment.status ?? '').toLowerCase() === 'unread',
            replyPreview: comment.comments ?? '',
            receivedLabel: fmtDate(comment.createdOn),
        };
    }

    private findComment(rowId: string): UserMessageComment | undefined {
        return this.comments.find(
            (comment: UserMessageComment) => String(comment.id) === rowId,
        );
    }

    completeDeleteConversation(topicId: any): void {
        this.crudService
            .deleteData(`/api/notifications/user-messages/topic-id/${topicId}`)
            .pipe(
                catchError((HttpError: HttpErrorResponse): any => {
                    this.isLoading = false;
                    this.crudError(HttpError);
                    return throwError(() => HttpError);
                }),
            )
            .subscribe(() => {
                this.threadOpen = false;
                this.snackbarService.dismissAll();
                this.snackbarService.success(
                    this.translate.instant('MyMessages.deleteSuccess'),
                );
                this.loadPage();
            });
    }

    ngOnDestroy(): void {
        this.mobile$.next();
        this.mobile$.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
